import { Group, Textarea } from '@mantine/core';
import { MAX_CERTIFICATION_TEXT_LENGTH } from '@dealstack/shared/lib/common/business/kycCertification';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useCertificationTextEditor } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/hooks/useCertificationTextEditor.ts';
import type { CertificationTextSource } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/types.ts';
import {
	Description,
	TextContent,
	Title,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/styles.ts';

interface Props {
	source: CertificationTextSource;
	disabled: boolean;
	/** No proof asks for a certified copy, so the wording is stored but nothing reads it. */
	isUnused?: boolean;
}

export const CertificationTextEditor = ({ source, disabled, isUnused }: Props) => {
	const tMipManage = useTranslationMipManage();
	const editor = useCertificationTextEditor(source);

	return (
		<TextContent>
			<Title>{tMipManage('kyc_settings.certification.text.label')}</Title>
			<Description>
				{isUnused
					? tMipManage('kyc_settings.certification.text.unused', {
							defaultValue:
								'No proof currently requires a certified copy, so this wording is not used.',
						})
					: tMipManage('kyc_settings.certification.text.description')}
			</Description>
			<Textarea
				value={editor.value}
				onChange={(event) => editor.setValue(event.currentTarget.value)}
				maxLength={MAX_CERTIFICATION_TEXT_LENGTH}
				minRows={3}
				autosize
				disabled={disabled}
				description={`${editor.value.length}/${MAX_CERTIFICATION_TEXT_LENGTH}`}
				inputWrapperOrder={['input', 'description']}
				data-testid="kyc-settings-certification-text"
			/>
			<Group justify="right" gap="xs">
				<ButtonV2
					size="s"
					buttonTheme="light"
					variant="secondary"
					disabled={disabled || editor.isSaving}
					onClick={editor.resetToDefault}
				>
					{tMipManage('kyc_settings.certification.text.reset')}
				</ButtonV2>
				<ButtonV2
					size="s"
					buttonTheme="light"
					variant="primary"
					disabled={disabled || !editor.isDirty || !editor.isValid || editor.isSaving}
					onClick={editor.save}
					data-testid="kyc-settings-certification-text-save"
				>
					{tMipManage('kyc_settings.certification.text.save')}
				</ButtonV2>
			</Group>
		</TextContent>
	);
};
